import React from 'react';
import { ClipboardList, UserCheck, Plane, HardHat } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: ClipboardList,
    title: 'Define the Requirement',
    description:
      "Tell us the project location, scope, technical requirements and personnel needed.",
  },
  {
    number: '02',
    icon: UserCheck,
    title: 'Select the Right Personnel',
    description:
      'We identify suitable engineers, supervisors or technical specialists for the assignment.',
  },
  {
    number: '03',
    icon: Plane,
    title: 'Deploy to Site',
    description:
      "Personnel are mobilized to the project location and integrated with the customer's project organization.",
  },
  {
    number: '04',
    icon: HardHat,
    title: 'Support Project Execution',
    description:
      'Our personnel work on site supporting supervision, coordination, installation activities, commissioning and technical problem solving.',
  },
];

export const About = () => {
  return (
    <section id="about-us" data-testid="about-section" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* About Us */}
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              About Us
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800"></div>
          </div>

          <div className="space-y-6 max-w-4xl" data-testid="about-content">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              Xiamen Tower Technology is a foreign-owned engineering and technical services company based in Xiamen, China.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              We provide engineers, site supervisors and technical specialists for industrial projects across China, supporting machinery manufacturers, plant engineering companies and industrial contractors during installation, commissioning and project execution.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              Our role is to strengthen the customer's project team on site. Depending on the project, our personnel can supervise installation activities, coordinate local subcontractors, support commissioning, troubleshoot technical issues and assist with day-to-day project execution.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              Support can be arranged for a specific project phase or for longer assignments, from an individual specialist to a project team.
            </p>
          </div>
        </div>

        {/* How We Work */}
        <div id="how-we-work" className="max-w-6xl mx-auto mt-24 scroll-mt-24" data-testid="how-we-work">
          <div className="mb-14">
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
              How We Work
            </h3>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-800"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={idx}
                  data-testid={`how-we-work-step-${idx}`}
                  className="relative bg-white border border-gray-200 rounded-lg p-7 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                >
                  {/* Step number */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-md bg-blue-700 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200 tracking-tight">
                      {step.number}
                    </span>
                  </div>

                  <h4 className="text-sm font-bold tracking-wider text-blue-800 uppercase mb-3">
                    {step.title}
                  </h4>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
